import {DatePipe, DecimalPipe} from '@angular/common';
import {Injectable} from '@angular/core';
import {ChartConstants} from '@constants/pages/chart-constants';
import {ChartPieType} from '@constants/pages/user-defined-page/chart-pie-type';
import {LineChartSettingsParameters} from '@models/charts/line-chart-settings-parameters';
import {Signal} from '@models/dashboard/signal';
import {ChartSignalEntry} from '@models/pages/user-defined-page/chart-signal-entry';
import {TranslationService} from '@services/translation/translation.service';
import {ChartsHelperService} from '@services/charts/helper/charts-helper.service';
import * as _Highcharts from 'highcharts';

@Injectable({
  providedIn: 'root'
})
export class PieChartSettingsGeneratorService {

  private readonly HOVER_HALO_SIZE = 10;
  private readonly HOVER_HALO_OPACITY = 0.25;
  private readonly DONUT_INNER_SIZE = '60%';

  constructor(private decimalPipe: DecimalPipe,
              private datePipe: DatePipe,
              private translationService: TranslationService,
              private chartsHelperService: ChartsHelperService) {
  }

  public generateChartOptions(chartParameters: LineChartSettingsParameters): _Highcharts.Options {
    const self = this;
    const pieType: ChartPieType = chartParameters.chartSettings.pieChartType || ChartPieType.STANDARD;
    const seriesType = this.determineSeriesType(pieType);
    return {
      title: {
        text: undefined
      },
      chart: {
        type: seriesType,
        events: {
          render: (event) => this.renderChart(event)
        }
      },
      tooltip: {
        backgroundColor: ChartConstants.TOOLTIP.BACKGROUND_COLOR,
        borderWidth: ChartConstants.TOOLTIP.BORDER_WIDTH,
        borderColor: ChartConstants.TOOLTIP.BORDER_COLOR,
        headerFormat: '',
        pointFormatter: function () {
          return self.tooltipFormatter(chartParameters.portalLanguage, this);
        }
      },
      legend: {
        enabled: true
      },
      plotOptions: {
        series: {
          events: {
            legendItemClick: function () {
              return !chartParameters.isInEditMode;
            }
          },
          marker: {
            symbol: 'circle'
          },
          states: {
            hover: {
              enabled: true,
              halo: {
                size: this.HOVER_HALO_SIZE,
                opacity: this.HOVER_HALO_OPACITY
              }
            }
          }
        },
        pie: this.generatePiePlotOptions(pieType),
        variablepie: this.generatePiePlotOptions(pieType)
      },
      credits: {
        enabled: false,
        text: String(chartParameters.chartId)
      },
      exporting: {
        enabled: false
      },
      series: this.generateSeries(seriesType, chartParameters.chartSettings.signals, chartParameters.machineSignals, chartParameters.isTemplateWidget, chartParameters.isLineChart)
    };
  }

  private determineSeriesType(pieType: ChartPieType): any {
    return pieType === ChartPieType.VARIABLE_RADIUS ? 'variablepie' : 'pie';
  }

  private generatePiePlotOptions(pieType: ChartPieType): any {
    const self = this;
    const plotOptions: any = {
      allowPointSelect: true,
      cursor: 'pointer',
      showInLegend: true,
      dataLabels: {
        enabled: true,
        formatter: function () {
          return self.dataLabelFormatter(this);
        }
      }
    };

    if (pieType === ChartPieType.DONUT || pieType === ChartPieType.HALF_DONUT) {
      plotOptions.innerSize = this.DONUT_INNER_SIZE;
    }

    if (pieType === ChartPieType.HALF_DONUT) {
      plotOptions.startAngle = -90;
      plotOptions.endAngle = 90;
      plotOptions.center = ['50%', '75%'];
      plotOptions.size = '110%';
    } else {
      plotOptions.size = '75%';
    }

    return plotOptions;
  }

  private generateSeries(seriesType: string, signals: ChartSignalEntry[], machineSignals: Signal[], isTemplateWidget: boolean, isLineChart: boolean): any[] {
    const data: any[] = [];

    for (const signal of signals) {
      let decimalPlaces = signal.precision || 1;
      let unit = signal.unit;
      let name = this.translationService.translate.instant(signal.displayedText || signal.label || signal.signalName);
      let machineSignal: Signal | undefined = undefined;
      if (isTemplateWidget) {
        machineSignal = this.getMachineSignalByName(signal.signalName, machineSignals);
      } else {
        machineSignal = this.getMachineSignalById(signal.signalId, machineSignals);
      }
      if (machineSignal && machineSignal.id) {
        decimalPlaces = machineSignal.decimals;
        unit = machineSignal.unit;
        name = this.translationService.translate.instant(machineSignal.label);
      }
      const color = this.chartsHelperService.createHex(signal.colors.main, isLineChart);
      const point: any = {
        name: name,
        y: 0,
        color: color,
        signalId: signal.signalId,
        signalName: signal.signalName,
        unit: unit,
        precision: decimalPlaces
      };
      if (seriesType === 'variablepie') {
        point.z = 0;
      }
      data.push(point);
    }

    return [
      {
        type: seriesType,
        showInLegend: true,
        id: '',
        data: data,
        marker: {
          symbol: 'circle'
        }
      }];
  }

  private renderChart(event) {
    if (!event || !event.target) {
      return;
    }
    const chart = event.target;

    chart.width = null;
    chart.height = '100%';
    chart.chartIsReady = true;
  }

  private getMachineSignalById(id, machineSignals: Signal[]): Signal {
    const signal = Object.values(machineSignals).find(function (item: Signal) {
      return item.id === id;
    });
    if (!signal) {
      return new Signal('', '', '', '');
    }
    return signal;
  }

  private getMachineSignalByName(name: string, machineSignals: Signal[]): Signal {
    const signal = Object.values(machineSignals).find(function (item: Signal) {
      return item.name.toLowerCase() === name.toLowerCase();
    });
    if (!signal) {
      return new Signal('', '', '', '');
    }
    return signal;
  }

  private dataLabelFormatter(labelControl): string {
    const value = this.decimalPipe.transform(labelControl.point.y, '1.' + labelControl.point.precision + '-' + labelControl.point.precision,
        this.translationService.getActiveLanguage());
    return labelControl.point.name + ': ' + value + ' ' + (labelControl.point.unit || '');
  }

  private tooltipFormatter(language, tooltipControl): string {
    const value = this.decimalPipe.transform(tooltipControl.y, '1.' + tooltipControl.precision + '-' + tooltipControl.precision,
        this.translationService.getActiveLanguage());
    const percentage = this.decimalPipe.transform(tooltipControl.percentage, '1.1-1', this.translationService.getActiveLanguage());
    const timestamp = tooltipControl.options.ts ?
        '<span>' + this.datePipe.transform(new Date(tooltipControl.options.ts), 'mediumTime', undefined, language) + '</span><br/>' : '';
    return timestamp +
        '<span style="color:' + tooltipControl.color + '">●</span> ' + tooltipControl.name + ': <b>' + value + ' ' + (tooltipControl.unit || '') + '</b>' +
        ' (' + percentage + ' %)<br/>';
  }
}
